import { agentClient } from "../../lib/agentClient";
import { bearer } from "../../lib/accessToken";

export interface FormActionScope {
  token: string;
  orgId: string;
  /** The Revit the form is bound to: the action reads that one's open model. */
  sessionId: string;
  workflowId: string;
}

/**
 * The agent refused or failed. `reason` is the kit's code for why, when it gave one: the
 * form translates it. `message` is the agent's own wording, which is in one language only.
 */
export class FormActionError extends Error {
  constructor(readonly status: number, readonly reason: string, message: string) {
    super(message);
  }
}

/**
 * Run a start form's action (`POST /workflows/:id/form-actions/:name`): the kit reads the
 * selected Revit's model and writes the result at `path`. Resolves with what the kit answered.
 */
export async function runFormAction(
  scope: FormActionScope,
  name: string,
  input: { values: Record<string, unknown>; path: string },
): Promise<Record<string, unknown>> {
  const query = new URLSearchParams({ organization_id: scope.orgId }).toString();
  const response = await agentClient.fetch(
    `/workflows/${encodeURIComponent(scope.workflowId)}/form-actions/${encodeURIComponent(name)}?${query}`,
    {
      method: "POST",
      headers: { Authorization: bearer(scope.token), "Content-Type": "application/json" },
      body: JSON.stringify({ target_session_id: scope.sessionId, values: input.values, path: input.path }),
    },
  );
  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok) {
    const error = body?.["error"] as { message?: unknown; details?: { reason?: unknown } } | undefined;
    throw new FormActionError(
      response.status,
      typeof error?.details?.reason === "string" ? error.details.reason : "",
      typeof error?.message === "string" ? error.message : "",
    );
  }
  return body ?? {};
}
